var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import z from "zod";
import ContentLayout from "../../Layout/ContentLayout";
import CustomCard from "../../UI/CustomCard/CustomCard";
import { AlertError, AlertSuccess, AlertWarning, } from "../../UI/StandardAlerts/StandardAlerts";
import useService from "../../Hooks/useService";
import ServiceResult, { Loaders } from "../../UI/ServiceResult/ServiceResult";
import InfoItems from "../../UI/InfoItems/InfoItems";
import DateService from "../../../Services/Shared/Date.service";
import CalendarService from "../../../Services/Calendar.service";
import ListService from "../../../Services/Shared/List.service";
import A11yHelpers from "../../../../util/A11yHelpers";
import useConfirmation from "../../Hooks/useConfirmation";
import MeetingAvailabilityService from "../../../Services/MeetingAvailability.service";
import MeetingModalityDomainService from "../../../Domain/MeetingModality/MeetingModality.service";
import MeetingService from "../../../Services/Meeting.service";
import { SaveButton } from "../../UI/StandardButtons/StandardButtons";
import FAIcon from "../../UI/FAIcon/FAIcon";
import ProfilePicture from "../../Presentational/Person/ProfilePicture/ProfilePicture";
import { getMeetingsSchedulePath } from "./MeetingsRoutes";
import { advisorHomeLink, studentHomePath } from "../Advisor/AdvisorRoutes";
import useVirtualizedApp from "../../Hooks/useVirtualizedApp";
import useCurrentUser from "../../Hooks/useCurrentUser";
import MeetingStatusDomainService from "../../../Domain/MeetingStatus/MeetingStatus.service";
const PROFILE_PICTURE_SIZE_PX = 40;
const MEETINGS_DETAIL_SEARCH_PARAMS = z.object({
    id: z.coerce.number(),
});
const Content = ({ id }) => {
    const history = useHistory();
    const currentUser = useCurrentUser();
    const [meetingState, getMeeting] = useService(MeetingService.getMeeting);
    useEffect(() => {
        getMeeting(id);
    }, [getMeeting, id]);
    const [cancelMeetingState, cancelMeeting] = useService(CalendarService.cancel);
    const [appointmentSettingsState, getAppointmentSettings] = useService(MeetingAvailabilityService.getStaffAppointmentSettingsForPersonId);
    const [staffPersonId, setStaffPersonId] = useState(null);
    useEffect(() => {
        if (staffPersonId) {
            getAppointmentSettings(staffPersonId);
        }
    }, [getAppointmentSettings, staffPersonId]);
    const CancelMeetingAlert = ({ isMeetingCancelled, }) => {
        if (cancelMeetingState.hasError && !isMeetingCancelled) {
            return _jsx(AlertError, { message: cancelMeetingState.errorMessage });
        }
        if (cancelMeetingState.wasSuccessful && !isMeetingCancelled) {
            return _jsx(AlertSuccess, { message: "Your meeting has been cancelled." });
        }
        return null;
    };
    const CancelMeetingButtons = ({ meetingId, isMeetingCancelled, }) => {
        const [confirmation, confirmModals] = useConfirmation({
            confirmButtonLabel: "Cancel Meeting",
            confirmButtonClassName: "btn btn-danger",
            cancelButtonLabel: "Close",
        });
        const virtualizedApp = useVirtualizedApp();
        const handleCancelMeeting = () => __awaiter(void 0, void 0, void 0, function* () {
            confirmation(_jsx(_Fragment, { children: "Are you sure you want to cancel this meeting? This cannot be undone." })).then((confirmed) => __awaiter(void 0, void 0, void 0, function* () {
                if (confirmed) {
                    yield cancelMeeting(meetingId);
                }
            }));
        });
        if (cancelMeetingState.wasSuccessful || isMeetingCancelled) {
            return (_jsxs(_Fragment, { children: [_jsx("button", { className: "btn btn-primary ml-2", onClick: () => history.push(getMeetingsSchedulePath({})), disabled: meetingState.isLoading, children: "Book a New Meeting" }), _jsx("button", { className: "btn btn-outline-secondary ml-2", onClick: () => window.location.assign(virtualizedApp.name !== "student"
                            ? advisorHomeLink({ virtualizedAppName: virtualizedApp.name })
                            : studentHomePath()), disabled: meetingState.isLoading, children: "Back to Home" })] }));
        }
        return (_jsxs(_Fragment, { children: [confirmModals, _jsx(SaveButton, Object.assign({ type: "submit", overrideLabel: "Cancel Meeting", className: "btn btn-danger", isLoading: cancelMeetingState.isLoading, disabled: cancelMeetingState.isLoading }, A11yHelpers.onClickOrEnterOrSpace(() => handleCancelMeeting()))), _jsx("button", { className: "btn btn-outline-secondary ml-2", onClick: () => window.location.assign(virtualizedApp.name !== "student"
                        ? advisorHomeLink({ virtualizedAppName: virtualizedApp.name })
                        : studentHomePath()), disabled: meetingState.isLoading, children: "Back to Home" })] }));
    };
    return (_jsx(_Fragment, { children: _jsx(CustomCard, { className: "mt-4", children: _jsx(ServiceResult, { state: meetingState, loader: Loaders.overlay(), children: (meeting) => {
                    var _a, _b, _c;
                    setStaffPersonId(meeting.staff.personId);
                    // This will display the date as such [Sunday, June 23, 2024]
                    const meetingDate = DateService.custom(meeting.startTime, {
                        year: "numeric",
                        month: "long",
                        day: "2-digit",
                        weekday: "long",
                    });
                    // Meeting endTime is nullable, so we handle this scenario here
                    let meetingTimeSlot;
                    if (meeting.endTime) {
                        meetingTimeSlot = `${DateService.displayJustTime(meeting.startTime)} - ${DateService.displayJustTime(meeting.endTime)}`;
                    }
                    else {
                        meetingTimeSlot = DateService.displayJustTime(meeting.startTime);
                    }
                    let appointmentDetails;
                    if (!meeting.meetingModality) {
                        // In case this is a legacy created meeting, we show the default meeting location
                        appointmentDetails = {
                            label: "Location",
                            value: meeting.serviceDeliveryLocation
                                ? `${meeting.serviceDeliveryLocation.location.name} - ${meeting.serviceDeliveryLocation.name}`
                                : // This null pointer check is necessary due to some older meeting data apparently having neither office nor campus
                                    (_a = meeting.location) === null || _a === void 0 ? void 0 : _a.name,
                        };
                    }
                    else {
                        // We show different field labels and values depending on the provided modality
                        switch (meeting.meetingModality.id) {
                            case MeetingModalityDomainService.physical.id:
                                appointmentDetails = {
                                    label: "Location",
                                    value: meeting.serviceDeliveryLocation
                                        ? `${meeting.serviceDeliveryLocation.location.name} - ${meeting.serviceDeliveryLocation.name}`
                                        : // This null pointer check is necessary due to some older meeting data apparently having neither office nor campus
                                            (_b = meeting.location) === null || _b === void 0 ? void 0 : _b.name,
                                };
                                break;
                            case MeetingModalityDomainService.online.id:
                                appointmentDetails = {
                                    label: "Online Link",
                                    value: (_jsx(ServiceResult, { state: appointmentSettingsState, loader: Loaders.skeleton({ loaderHeight: "1rem" }), children: ({ onlineMeetingLink }) => {
                                            if (!onlineMeetingLink) {
                                                return "No meeting link has been provided. Please contact this staff member directly.";
                                            }
                                            return (_jsx(_Fragment, { children: _jsxs("a", { href: onlineMeetingLink, target: "_blank", rel: "noreferrer", children: [onlineMeetingLink, _jsx(FAIcon, { className: "ml-1", icon: "external-link", size: "xs" })] }) }));
                                        } })),
                                };
                                break;
                            // For now, we are not showing any information for phone modality
                            case MeetingModalityDomainService.phone.id:
                            default:
                                appointmentDetails = null;
                        }
                    }
                    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "card-header bg-white d-flex justify-content-between", children: _jsx("div", { className: "mb-0 card-title h5 w-100", style: {
                                        minHeight: "40px",
                                    }, children: _jsxs("div", { className: "d-flex flex-row align-items-center", children: [_jsxs("div", { className: "position-relative mr-2", style: {
                                                    width: `${PROFILE_PICTURE_SIZE_PX * 1.5}px`,
                                                    height: `${PROFILE_PICTURE_SIZE_PX}px`,
                                                }, children: [_jsx("div", { style: {
                                                            position: "absolute",
                                                            top: "0px",
                                                            left: `${PROFILE_PICTURE_SIZE_PX / 2}px`,
                                                            zIndex: 1,
                                                        }, children: _jsx(ProfilePicture, { person: meeting.student, size: PROFILE_PICTURE_SIZE_PX, showProfilePicture: true, className: "shadow" }) }), _jsx("div", { style: {
                                                            position: "absolute",
                                                            top: "0px",
                                                            left: "0px",
                                                            zIndex: 2,
                                                        }, children: _jsx(ProfilePicture, { person: meeting.staff, size: PROFILE_PICTURE_SIZE_PX, showProfilePicture: true, className: "shadow" }) })] }), _jsx("div", { children: (currentUser === null || currentUser === void 0 ? void 0 : currentUser.personId) === (meeting === null || meeting === void 0 ? void 0 : meeting.student.personId) ? (_jsxs(_Fragment, { children: ["Review Meeting with ", meeting.staff.fullName] })) : (_jsxs(_Fragment, { children: ["Review Meeting between ", meeting.staff.fullName, " and", " ", (_c = meeting.student) === null || _c === void 0 ? void 0 : _c.fullName] })) })] }) }) }), _jsxs("div", { className: "card-body", children: [meeting.meetingStatus.id ===
                                        MeetingStatusDomainService.Cancelled.id && (_jsx(AlertWarning, { message: "This meeting has been cancelled." })), _jsx(InfoItems, { items: [
                                            {
                                                label: "Date",
                                                value: meetingDate,
                                            },
                                            {
                                                label: "Time",
                                                value: meetingTimeSlot,
                                            },
                                            {
                                                label: "Staff",
                                                value: meeting.staff.fullName,
                                            },
                                            appointmentDetails,
                                            {
                                                label: "Service",
                                                value: meeting.meetingServiceOfferings
                                                    ? ListService.displayWithCommasAndAnd(meeting.meetingServiceOfferings.map((so) => so.serviceOffering.name), 5)
                                                    : null,
                                            },
                                            {
                                                label: "How do you want to meet?",
                                                value: meeting.meetingModality && (_jsx("span", { className: "badge badge-secondary mr-2", children: meeting.meetingModality.name })),
                                            },
                                            {
                                                label: "Would you like to share anything else?",
                                                value: meeting.studentQuestion,
                                            },
                                        ] }), _jsx(CancelMeetingAlert, { isMeetingCancelled: meeting.meetingStatus.id ===
                                            MeetingStatusDomainService.Cancelled.id })] }), _jsx("div", { className: "card-footer d-flex justify-content-end", children: _jsx(CancelMeetingButtons, { meetingId: meeting.id, isMeetingCancelled: meeting.meetingStatus.id ===
                                        MeetingStatusDomainService.Cancelled.id }) })] }));
                } }) }) }));
};
const MeetingsDetail = () => {
    const params = useParams();
    const safeSearchParams = MEETINGS_DETAIL_SEARCH_PARAMS.safeParse(params);
    return (_jsx(ContentLayout, { notFluid: true, paddingBottom: true, paddingTop: true, className: "fullMinHeightMinusPrimaryNav", children: safeSearchParams.success ? (_jsx(Content, Object.assign({}, safeSearchParams.data))) : (_jsx(CustomCard, { children: _jsx("div", { className: "card-body", children: _jsx(AlertError, { message: "Invalid params" }) }) })) }));
};
export default MeetingsDetail;
